import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { contentApi } from '../services/api'

interface Content {
  hero?: {
    title?: string
    subtitle?: string
    description?: string
    cta_primary?: string
    cta_secondary?: string
  }
  collections?: {
    title?: string
    subtitle?: string
    boys?: {
      title?: string
      description?: string
    }
    girls?: {
      title?: string
      description?: string
    }
  }
  about?: {
    title?: string
    description?: string
    stats?: {
      years?: number
      families?: number
    }
  }
  contact?: {
    whatsapp?: string
  }
  seasons?: any
}

interface ContentContextType {
  content: Content
  isLoading: boolean
  error: string | null
  reloadContent: (forceReload?: boolean) => Promise<void>
}

const defaultContent: Content = {
  hero: {
    title: 'Jenny Kids',
    subtitle: 'Ropa infantil moderna y cómoda para tus pequeños',
    description: 'Descubre nuestras colecciones únicas diseñadas especialmente para niños y niñas. Calidad, estilo y comodidad en cada prenda.',
    cta_primary: 'Ver Colecciones',
    cta_secondary: 'Suscribirse'
  },
  collections: {
    title: 'Nuestras Colecciones',
    subtitle: 'Descubre nuestra amplia gama de ropa infantil diseñada para la comodidad y el estilo de tus pequeños',
    boys: {
      title: 'Colección Niños',
      description: 'Ropa cómoda y resistente para los más activos. Diseños modernos que combinan estilo y funcionalidad.'
    },
    girls: {
      title: 'Colección Niñas',
      description: 'Vestidos, blusas y conjuntos elegantes que realzan la personalidad única de cada niña.'
    }
  },
  about: {
    title: 'Sobre Jenny Kids',
    description: 'Somos una marca especializada en ropa infantil que combina moda, comodidad y calidad. Nuestra misión es vestir a los más pequeños con prendas que les permitan expresar su personalidad mientras se sienten cómodos y seguros.',
    stats: {
      years: 10,
      families: 1000
    }
  },
  contact: {
    whatsapp: '573507835575' // Luis Gutierrez - Jenny Kids (Colombia +57)
  }
}

const ContentContext = createContext<ContentContextType | undefined>(undefined)

interface ContentProviderProps {
  children: ReactNode
}

export const ContentProvider: React.FC<ContentProviderProps> = ({ children }) => {
  const [content, setContent] = useState<Content>(defaultContent)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadContent = async (forceReload = false) => {
    try {
      setIsLoading(true)
      setError(null)
      
      // Forzar recarga sin caché si se solicita
      const response = await contentApi.getAll(forceReload)

      // Manejar diferentes formatos de respuesta
      let contentData = null

      if (response && response.data) {
        contentData = response.data
      } else if (response && typeof response === 'object' && !response.data) {
        contentData = response
      } else {
        contentData = defaultContent
      }

      // Asegurar que siempre tenemos un objeto válido
      if (contentData && typeof contentData === 'object') {
        // Forzar nueva referencia para que React detecte el cambio
        const newContent = { ...contentData } as Content
        setContent(newContent)
      } else {
        setContent({ ...defaultContent })
      }
      
    } catch (err) {
      setError('Error al cargar el contenido')
      // Mantener contenido por defecto en caso de error
      setContent(defaultContent)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadContent()
    
    // Escuchar evento de actualización de contenido
    const handleContentUpdate = () => {
      loadContent(true) // Forzar recarga sin caché
    }

    window.addEventListener('contentUpdated', handleContentUpdate)
    
    return () => {
      window.removeEventListener('contentUpdated', handleContentUpdate)
    }
  }, [])

  return (
    <ContentContext.Provider value={{ content, isLoading, error, reloadContent: loadContent }}>
      {children}
    </ContentContext.Provider>
  )
}

export const useContent = (): ContentContextType => {
  const context = useContext(ContentContext)
  if (context === undefined) {
    throw new Error('useContent debe ser usado dentro de un ContentProvider')
  }
  return context
}
